/** @jsxImportSource @emotion/react */
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSkeletonText,
  EuiSpacer,
  EuiTitle,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { Project } from "graphql/_Types";
import { USER_ACCESS_LEVELS_BY_ID } from "types/UserAccessLevels";
import { getProjectDjango } from "django/libraryProjectDjango";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "django/queryKeys";
import { getPermissionData } from "components/ProjectSharing/ProjectSharing";
import { permissions } from "components/ProjectSharing/permissions";
import { RoleBadge } from "components/RoleBadge/RoleBadge";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { css } from "@emotion/react";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";

export interface ToolPanelAccessProps {
  projectId: Project["id"];
}

/*
 * Project page sidebar panel with sharing/access information
 */
export const ToolPanelAccess = ({ projectId }: ToolPanelAccessProps) => {
  const { data, isLoading, isRefetchError, isLoadingError } = useQuery({
    queryFn: () =>
      getPermissionData(
        projectId,
        getProjectDjango,
        permissions.userFunctions.internalAccess
          .getInternalUsersWithProjectAccess,
        permissions.userFunctions.externalAccess
          .getProjectSharingExternalInvitations,
      ),
    queryKey: queryKeys.modalProjectShare(projectId),
    refetchOnMount: "always",
  });

  const projectOwner = useDataTableContext((s) => s.project.user.id);
  const { euiTheme } = useEuiTheme();
  const styles = {
    dl: css`
      display: grid;
      grid-template-columns: max-content auto;
    `,
    dt: css`
      grid-column-start: 1;
      margin-right: 0.5rem;
      font-weight: bold;
      padding: 5px 0px;
    `,
    dd: css`
      padding: 5px 0px;
    `,
    subContent: css`
      color: ${euiTheme.colors.subduedText};
    `,
  };

  if (isLoading) {
    return <EuiSkeletonText lines={5} />;
  }

  if (data === undefined || isRefetchError || isLoadingError) {
    return null;
  }

  const defaultAccessLevel = data.project.default_user_access_level;
  const defaultAccessRole = USER_ACCESS_LEVELS_BY_ID[defaultAccessLevel].name;

  return (
    <EuiPanel
      hasBorder={false}
      hasShadow={false}
      paddingSize="s"
      style={{ flexGrow: "0" }}
    >
      <EuiFlexGroup responsive={false}>
        <EuiFlexItem>
          <EuiTitle size="s">
            <span>Access</span>
          </EuiTitle>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="s" />
      <dl css={styles.dl}>
        <dt css={styles.dt}>Owner</dt>
        <dd css={styles.dd}>
          <UserAvatar userId={projectOwner} size="s" />
        </dd>

        <dt css={styles.dt}>Organization</dt>
        <dd css={styles.dd}>
          <RoleBadge role={defaultAccessRole} />
        </dd>
      </dl>
    </EuiPanel>
  );
};
